import React from 'react';
import LazyAnimatedTitle from '../LazyAnimatedTitle';
import LazyLogo3D from '../Header/LazyLogo3D';
import { WhitelistForm } from '../Form';
import performanceMonitor from '../../utils/performanceMonitor';

interface MainLayoutProps {
  className?: string;
}

const MainLayout: React.FC<MainLayoutProps> = ({ className = '' }) => {

  return (
    <div className={`min-h-screen bg-gradient-main flex flex-col ${className}`}>

      {/* Header Section */}
      <header className="flex-shrink-0 py-8 px-4 sm:py-12 sm:px-6 lg:px-8 relative">
        {/* Admin Button - Top Right */}
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
          <button
            onClick={() => window.location.href = '/admin'}
            className="group relative flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-green-light to-primary-green hover:from-primary-green hover:to-primary-green-secondary backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl border border-white border-opacity-20 hover:border-opacity-40 transition-all duration-300 hover:scale-105 active:scale-95"
            title="Admin Panel"
          >
            {/* Glow effect */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary-green-light to-primary-green opacity-0 group-hover:opacity-30 blur-md transition-opacity duration-300"></div>
            
            {/* Icon */}
            <svg 
              className="relative w-5 h-5 sm:w-6 sm:h-6 text-white drop-shadow-sm transition-transform duration-300 group-hover:rotate-12" 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
            </svg>
            
            {/* Tooltip */}
            <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-black bg-opacity-80 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
              Admin Panel
            </div>
          </button>
        </div>

        <div className="max-w-4xl mx-auto text-center">
          {/* Animated Title */}
          <div className="mb-4 sm:mb-6">
            <LazyAnimatedTitle
              text="$PGC Whitelist Registration"
              className="mb-4"
            />
          </div>

          {/* Logo */}
          <div className="mb-8 sm:mb-12">
            <LazyLogo3D
              size="large"
              className="mx-auto"
              logoUrl="/apps/whitelist/LogoPetgasCoinTransparent.png"
            />
          </div>

          {/* Descriptive Text */}
          <div className="mb-6 sm:mb-8">
            <p className="text-base sm:text-lg text-text-light opacity-90 max-w-3xl mx-auto leading-relaxed">
              Limited-time airdrop🔥 Get your $PGC now. We're building a sustainable future by turning plastic waste into clean energy using blockchain technology.
            </p>
          </div>
        </div>
      </header>

      {/* Main Content Section */}
      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
        <div className="w-full max-w-lg">
          <WhitelistForm 
            onSubmitSuccess={(data) => {
              performanceMonitor.endLoadTimeTracking('form-submission');
              console.log('Registration successful:', data);
              // Additional success handling can be added here
            }}
            onSubmitError={(error) => {
              performanceMonitor.endLoadTimeTracking('form-submission');
              console.error('Registration error:', error);
              
              // Show user-friendly error message for database issues
              if (error.type === 'DATABASE_ERROR') {
                console.log(`
🔧 Database Setup Required:

It looks like the database tables haven't been created yet.

To fix this:
1. Go to your Supabase project dashboard
2. Navigate to SQL Editor  
3. Run the SQL script from supabase-schema.sql
4. Make sure you're using the correct anon key in your .env file

Check the browser console for more details.
                `);
              }
            }}
            className="w-full"
          />
        </div>
      </main>

      {/* Footer Section */}
      <footer className="flex-shrink-0 py-8 px-4 sm:px-6 lg:px-8 border-t border-primary-green border-opacity-30">
        <div className="max-w-4xl mx-auto">
          {/* Footer Content */}
          <div className="text-center space-y-6">
            {/* Social Links */}
            <div className="flex justify-center space-x-6">
              <a 
                href="https://petgascoin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-text-light hover:text-gold transition-colors duration-200"
              >
                <span className="sr-only">Website</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </a>
              <a 
                href="#" 
                className="text-text-light hover:text-gold transition-colors duration-200"
              >
                <span className="sr-only">Twitter</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </a>
              <a 
                href="#" 
                className="text-text-light hover:text-gold transition-colors duration-200"
              >
                <span className="sr-only">Telegram</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
              </a>
            </div>

            {/* Footer Text */}
            <div className="space-y-2">
              <p className="text-sm text-text-light opacity-75">
                © 2024 PETGASCOIN. Building a sustainable future through blockchain innovation.
              </p>
              <p className="text-xs text-text-light opacity-60">
                Join our community and be part of the green revolution in cryptocurrency.
              </p>
            </div>

            {/* Contact Information */}
            <div className="pt-4 border-t border-primary-green border-opacity-20">
              <p className="text-xs text-text-light opacity-60">
                Questions? Contact us at{' '}
                <a 
                  href="mailto:contact@petgascoin.com" 
                  className="text-gold hover:text-gold-light transition-colors duration-200"
                >
                  contact@petgascoin.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLayout;